import { createEntityAdapter, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import SortDirection from '@rio-cloud/rio-uikit/utils/SortUtils';

import { chatApi } from '@/services/chatApi/chatApi';
import { Response } from '@/services/config/types';
import { threadApi } from '@/services/threadApi/threadApi';
import { categorizeByDate } from '@/utils/categorizeLists/categorizeByDate';
import { TCategorizedByDate } from '@/utils/categorizeLists/types';
import { ONBOARDING_THREAD_ID } from '@/utils/constants';
import Logger from '@/utils/logger';
import { isSortDirection } from '@/utils/sortHelper/sortHelper';
import { createTemporaryThreadId, isTemporaryThreadId } from '@/utils/temporaryUtils/temporaryIds';

import { setCurrentLayout } from '../layout/layoutSlice';
import { handleNextOnboardingStep } from '../onboarding/onboardingSlice';
import { RootState } from '../store';
import {
  EActiveThreadRestoreStatus,
  TChatThread,
  ThreadData,
  ThreadPagination,
  ThreadsState,
  TUpdateThreadPayload,
} from './types';

const LOGGER_TAG = 'ThreadSlice';

export const ACTIVE_THREAD_STORAGE_KEY = 'activeThreadId';

export const DEFAULT_PAGINATION: ThreadPagination = {
  totalThreads: 0,
  totalPages: 0,
  currentPage: 1,
  limit: 25,
  hasNextPage: false,
  hasPreviousPage: false,
  sortOrder: SortDirection.DESCENDING,
  onlyFavorites: undefined,
};

const persistedActiveThreadId = sessionStorage.getItem(ACTIVE_THREAD_STORAGE_KEY);

const initialActiveThreadId =
  persistedActiveThreadId &&
  persistedActiveThreadId !== ONBOARDING_THREAD_ID &&
  !isTemporaryThreadId(persistedActiveThreadId)
    ? persistedActiveThreadId
    : null;

const threadsAdapter = createEntityAdapter<TChatThread, string>({
  selectId: (thread) => thread.threadId,
  sortComparer: (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
});

const createTodayThread = (): TChatThread => {
  const today = new Date().toISOString();
  return {
    threadId: createTemporaryThreadId(),
    title: '',
    timestamp: today,
    isFavorite: false,
  };
};

const initialState: ThreadsState & { threads: ReturnType<typeof threadsAdapter.getInitialState> } = {
  threads: threadsAdapter.getInitialState(),
  activeThreadId: initialActiveThreadId,
  loadingThreadIds: [],
  isFetchingHistory: false,
  isAllThreadsInitialFetch: true,
  isFavoritesInitialFetch: true,
  pagination: DEFAULT_PAGINATION,
  // INFO: With nothing persisted there is nothing to restore, so the lifecycle starts terminal
  // and the chat empty states are allowed to render on the very first paint.
  activeThreadRestoreStatus: initialActiveThreadId ? EActiveThreadRestoreStatus.IDLE : EActiveThreadRestoreStatus.DONE,
};

const threadSelectors = threadsAdapter.getSelectors<RootState>((state) => state.thread.threads);

export const selectAllThreads = threadSelectors.selectAll;

const selectFavoriteThreads = createSelector(selectAllThreads, (threads) =>
  threads.filter((thread) => thread.isFavorite),
);

const threadSlice = createSlice({
  name: 'threads',
  initialState,
  reducers: {
    resetActiveThread: (state) => {
      state.activeThreadId = null;
      state.activeThreadRestoreStatus = EActiveThreadRestoreStatus.DONE;
      sessionStorage.removeItem(ACTIVE_THREAD_STORAGE_KEY);
    },
    addNewThread: (state) => {
      const newThread = createTodayThread();
      threadsAdapter.addOne(state.threads, newThread);
      state.activeThreadId = newThread.threadId;
      state.activeThreadRestoreStatus = EActiveThreadRestoreStatus.DONE;
      sessionStorage.setItem(ACTIVE_THREAD_STORAGE_KEY, newThread.threadId);
      state.pagination.totalThreads += 1;
    },
    setActiveThread: (state, action: PayloadAction<string>) => {
      state.activeThreadId = action.payload;
      state.activeThreadRestoreStatus = EActiveThreadRestoreStatus.DONE;
      sessionStorage.setItem(ACTIVE_THREAD_STORAGE_KEY, action.payload);
    },
    updateThreadTitle: (state, action: PayloadAction<{ title: string }>) => {
      const { title } = action.payload;
      const tempThreadId = Object.keys(state.threads.entities).find(isTemporaryThreadId);

      if (tempThreadId) {
        threadsAdapter.updateOne(state.threads, {
          id: tempThreadId,
          changes: { title },
        });
      }
    },
    toggleFavorite: (state, action: PayloadAction<string>) => {
      const thread = state.threads.entities[action.payload];
      if (thread) {
        threadsAdapter.updateOne(state.threads, {
          id: action.payload,
          changes: { isFavorite: !thread.isFavorite },
        });
      }
    },

    // TODO: Upon refactor, reuse this reducer to update both the title and the favorite status of a thread. TEMP for FIBOT-1434
    updateThread: (state, action: PayloadAction<TUpdateThreadPayload>) => {
      const { threadId, updatedThread } = action.payload;
      const thread = state.threads.entities[threadId];

      if (!threadId || !thread) {
        Logger.error(LOGGER_TAG, 'Thread matching id not found', action.payload);
        return;
      }
      threadsAdapter.updateOne(state.threads, { id: threadId, changes: updatedThread });
    },

    removeOnboardingThread: (state) => {
      const onboardingThreadId = 'onboarding-thread';
      threadsAdapter.removeOne(state.threads, onboardingThreadId);
      state.activeThreadId = null;
      state.activeThreadRestoreStatus = EActiveThreadRestoreStatus.DONE;
      sessionStorage.removeItem(ACTIVE_THREAD_STORAGE_KEY);
    },
    clearThreadLoading: (state, action: PayloadAction<string>) => {
      state.loadingThreadIds = state.loadingThreadIds.filter((id) => id !== action.payload);
    },
    reconcileServerThread: (state, action: PayloadAction<{ serverThreadId: string }>) => {
      const { serverThreadId } = action.payload;
      // INFO: Having multiple optimistic threads is not predictable since addNewThread is only triggered when no activeThread exists
      const optimisticThreadId = Object.keys(state.threads.entities).find(isTemporaryThreadId);

      if (!optimisticThreadId) return;

      const optimisticThread = state.threads.entities[optimisticThreadId];
      if (!optimisticThread) return;

      threadsAdapter.removeOne(state.threads, optimisticThreadId);
      threadsAdapter.upsertOne(state.threads, { ...optimisticThread, threadId: serverThreadId });

      if (state.activeThreadId === optimisticThreadId) {
        state.activeThreadId = serverThreadId;
        sessionStorage.setItem(ACTIVE_THREAD_STORAGE_KEY, serverThreadId);
      }

      state.loadingThreadIds = state.loadingThreadIds.filter((id) => id !== optimisticThreadId);
    },
    startActiveThreadRestore: (state) => {
      state.activeThreadRestoreStatus = EActiveThreadRestoreStatus.PENDING;
    },
    finishActiveThreadRestore: (state) => {
      state.activeThreadRestoreStatus = EActiveThreadRestoreStatus.DONE;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setCurrentLayout, (state, action) => {
      if (action.payload === 'onboardingMode') {
        state.activeThreadId = null;
        state.activeThreadRestoreStatus = EActiveThreadRestoreStatus.DONE;
      }
    });
    builder.addCase(handleNextOnboardingStep, (state) => {
      const onboardingThreadId = 'onboarding-thread';
      const existingOnboardingThread = state.threads.entities[onboardingThreadId];

      if (!existingOnboardingThread) {
        const newOnboardingThread: TChatThread = {
          threadId: onboardingThreadId,
          title: 'Demo',
          timestamp: new Date().toISOString(),
          isFavorite: false,
        };
        threadsAdapter.addOne(state.threads, newOnboardingThread);
      }

      state.activeThreadId = onboardingThreadId;
      state.activeThreadRestoreStatus = EActiveThreadRestoreStatus.DONE;
      sessionStorage.setItem(ACTIVE_THREAD_STORAGE_KEY, onboardingThreadId);
    });
    builder.addMatcher(
      threadApi.endpoints.fetchThreads.matchFulfilled,
      (state, action: PayloadAction<Response<ThreadData>>) => {
        state.isFetchingHistory = false;
        if (action.payload.data?.pagination.onlyFavorites) {
          state.isAllThreadsInitialFetch = true;
          state.isFavoritesInitialFetch = false;
        } else {
          state.isAllThreadsInitialFetch = false;
          state.isFavoritesInitialFetch = true;
        }
        if (action.payload.data?.threads) {
          if (action.payload.data.pagination.currentPage === 1) {
            // INFO: Preserve the optimistic temp thread across removeAll so it survives when fetchThreads resolves while sendMessage is in-flight
            const optimisticThread = Object.values(state.threads.entities).find(
              (t) => t && isTemporaryThreadId(t.threadId),
            );
            threadsAdapter.removeAll(state.threads);
            if (optimisticThread) {
              threadsAdapter.addOne(state.threads, optimisticThread);
            }
          }
          threadsAdapter.upsertMany(state.threads, action.payload.data.threads);

          const activeThreadFromStorage = sessionStorage.getItem(ACTIVE_THREAD_STORAGE_KEY);
          const optimisticThreadId = Object.keys(state.threads.entities).find(isTemporaryThreadId);

          // INFO: Prioritize optimistic thread as active if it exists (sendMessage in-flight), otherwise restore from sessionStorage
          if (optimisticThreadId) {
            state.activeThreadId = optimisticThreadId;
          } else if (activeThreadFromStorage && state.threads.entities[activeThreadFromStorage]) {
            state.activeThreadId = activeThreadFromStorage;
          }
          state.pagination = action.payload.data.pagination;
        }
      },
    );

    builder.addMatcher(chatApi.endpoints.sendMessage.matchPending, (state) => {
      state.loadingThreadIds.push(state.activeThreadId);
    });

    builder.addMatcher(chatApi.endpoints.sendMessage.matchRejected, (state) => {
      state.loadingThreadIds = state.loadingThreadIds.filter((id) => id !== state.activeThreadId);
    });

    builder.addMatcher(threadApi.endpoints.fetchThreads.matchPending, (state) => {
      state.isFetchingHistory = true;
    });

    builder.addMatcher(threadApi.endpoints.fetchThreads.matchRejected, (state) => {
      state.isFetchingHistory = false;
    });

    builder.addMatcher(threadApi.endpoints.deleteThread.matchFulfilled, (state, action) => {
      const deletedThreadId = action.meta.arg.originalArgs.threadId;
      const threadToDelete = state.threads.entities[deletedThreadId];
      if (threadToDelete) {
        threadsAdapter.removeOne(state.threads, deletedThreadId);
        state.pagination.totalThreads = state.pagination.totalThreads - 1 > 0 ? state.pagination.totalThreads - 1 : 0;
      }

      if (state.activeThreadId === deletedThreadId) {
        state.activeThreadId = null;
        state.activeThreadRestoreStatus = EActiveThreadRestoreStatus.DONE;
        sessionStorage.removeItem(ACTIVE_THREAD_STORAGE_KEY);
      }
    });

    builder.addMatcher(threadApi.endpoints.deleteThreads.matchFulfilled, (state) => {
      const favoriteThreads = selectFavoriteThreads({ thread: state } as RootState);
      threadsAdapter.removeAll(state.threads);
      threadsAdapter.addMany(state.threads, favoriteThreads);
      state.activeThreadId = null;
      state.activeThreadRestoreStatus = EActiveThreadRestoreStatus.DONE;
      sessionStorage.removeItem(ACTIVE_THREAD_STORAGE_KEY);
      state.pagination.totalThreads = 0;
      state.pagination.hasNextPage = false;
    });
  },
});

export const {
  addNewThread,
  resetActiveThread,
  setActiveThread,
  updateThreadTitle,
  toggleFavorite,
  removeOnboardingThread,
  updateThread,
  reconcileServerThread,
  clearThreadLoading,
  startActiveThreadRestore,
  finishActiveThreadRestore,
} = threadSlice.actions;

export default threadSlice.reducer;

export const selectThreadById = threadSelectors.selectById;
export const selectThreadIds = threadSelectors.selectIds;

// INFO: Resolves the active thread entity, so it stays null until the paginated thread list has
// loaded. selectActiveThreadId should be used when only the id is needed becaause it i available on the first
// render after a reload since it is seeded from sessionStorage.
export const selectActiveThread = createSelector(
  [(state: RootState) => state.thread.activeThreadId, threadSelectors.selectAll],
  (activeThreadId, allThreads) => {
    return allThreads.find((thread) => thread.threadId === activeThreadId) || null;
  },
);
export const selectActiveThreadId = (state: RootState) => state.thread.activeThreadId;

export const selectIsThreadLoading = (id: string | null) => (state: RootState) =>
  state.thread.loadingThreadIds.includes(id);

export const selectIsHistoryOfThreadsFetching = (state: RootState) => state.thread.isFetchingHistory;
export const selectIsAllThreadsInitialFetch = (state: RootState) => state.thread.isAllThreadsInitialFetch;
export const selectIsFavoritesInitialFetch = (state: RootState) => state.thread.isFavoritesInitialFetch;
export const selectIsDemoThread = (threadId: string) => (state: RootState) => {
  const thread = threadSelectors.selectById(state, threadId);
  return thread ? thread.title.startsWith('DEMO') : false;
};

export const selectPagination = (state: RootState) => state.thread.pagination;

const selectThreadsInTimespan = createSelector(
  [selectAllThreads, selectPagination],
  (threads, pagination): TCategorizedByDate<TChatThread> => {
    const sortOrder = isSortDirection(pagination.sortOrder) ? pagination.sortOrder : SortDirection.DESCENDING;
    return categorizeByDate(threads, sortOrder);
  },
);

const selectFavoriteThreadsInTimespan = createSelector(
  [selectFavoriteThreads, selectPagination],
  (threads, pagination) => {
    const sortOrder = isSortDirection(pagination.sortOrder) ? pagination.sortOrder : SortDirection.DESCENDING;
    return categorizeByDate(threads, sortOrder);
  },
);

export const selectCategorizedThreads = (isFavorite: boolean) =>
  isFavorite ? selectFavoriteThreadsInTimespan : selectThreadsInTimespan;

export const selectAllLoadedThreads = createSelector(selectAllThreads, (threads) => threads.length);

export const selectAllThreadsLoading = createSelector(
  (state: RootState) => state.thread.loadingThreadIds,
  (state: RootState) => state.thread.threads.entities,
  (loadingThreadIds, entities) => loadingThreadIds.map((id) => (id != null && entities[id] ? id : 'fallback')),
);

export const selectIsActiveThreadRestorePending = (state: RootState) =>
  state.thread.activeThreadRestoreStatus !== EActiveThreadRestoreStatus.DONE;
